import React, { useState } from 'react';
import { Check, X, Lock, ChevronDown, ChevronUp } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/3arfjYMhtjw4AWcoSjx5Sf/Guia_Anti_Inflamacion_Dorada.png';
export function SalesPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: '¿Necesito comprar ingredientes caros?',
    a: 'No, todo se encuentra en un supermercado normal por unos pocos centavos.'
  }, {
    q: '¿Es difícil de leer?',
    a: 'He usado una letra grande y clara, diseñada específicamente para que sea fácil de leer en tablets y teléfonos.'
  }, {
    q: '¿Puedo hacerlo si tomo otros medicamentos?',
    a: 'El protocolo es natural, pero siempre incluimos una sección para que lo consultes con tu médico.'
  }, {
    q: '¿Cuánto tardo en ver resultados?',
    a: 'La mayoría de las usuarias reportan mayor movilidad en su primera semana.'
  }];
  return <main className="min-h-screen w-full overflow-x-hidden selection:bg-amber-500 selection:text-white" data-id="element-1">
      {/* SECTION 1 — HERO */}
      <section className="bg-cream-200 py-16 md:py-24 px-6 border-b-4 border-amber-500/20" data-id="element-2">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12" data-id="element-3">
          <div className="flex-1 space-y-6 text-center md:text-left" data-id="element-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brown-900 leading-tight" data-id="element-5">
              Despierta Sin Dolor: El Protocolo de las 3 Especias para tus
              Articulaciones
            </h1>
            <h2 className="text-xl md:text-2xl text-brown-800 font-medium max-w-2xl mx-auto md:mx-0" data-id="element-6">
              Recupera la movilidad de tus manos y rodillas en solo 7 días con
              el secreto natural que tu cocina ya esconde.
            </h2>
            <div className="pt-4" data-id="element-7">
              <a href="#offer" onClick={e => {
              e.preventDefault();
              document.getElementById('offer')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="inline-flex items-center justify-center w-full md:w-auto px-8 py-4 bg-cta hover:bg-cta-hover text-white text-xl font-bold rounded-lg shadow-lg transition-colors duration-300" data-id="element-8">
                QUIERO DESPERTAR SIN DOLOR
              </a>
            </div>
          </div>
          <div className="flex-1 w-full max-w-md mx-auto md:max-w-none" data-id="element-9">
            <img src={PRODUCT_IMAGE} alt="Guía Anti-Inflamación Dorada en una tablet" className="w-full h-auto drop-shadow-2xl rounded-xl" data-id="element-10" />
          </div>
        </div>
      </section>

      {/* SECTION 2 — OPENING HOOK */}
      <section className="py-16 md:py-24 px-6" data-id="element-11">
        <div className="max-w-3xl mx-auto space-y-8 text-lg md:text-xl text-brown-800" data-id="element-12">
          <p className="font-heading text-2xl md:text-3xl text-amber-600 font-semibold text-center mb-12" data-id="element-13">
            ¿Recuerdas cómo se sentía levantarse de la cama y simplemente
            caminar hacia la cocina sin pensar en el dolor?
          </p>
          <p data-id="element-14">
            Para muchas de nosotras, ese recuerdo parece lejano. Hoy, el simple
            acto de abrir un frasco o bajar una escalera se ha convertido en una
            batalla contra nuestro propio cuerpo.
          </p>
          <p data-id="element-15">
            No es que seas "vieja". Es que el fuego de la inflamación en tus
            coyunturas se ha quedado encendido y nadie te ha enseñado cómo
            apagarlo de forma natural.
          </p>
        </div>
      </section>

      {/* SECTION 3 — THE PROBLEM */}
      <section className="py-16 md:py-24 px-6 bg-cream-200" data-id="element-16">
        <div className="max-w-3xl mx-auto space-y-8" data-id="element-17">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-brown-900" data-id="element-18">
            El dolor que te roba las mañanas
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-brown-800" data-id="element-19">
            <p data-id="element-20">
              Ese momento a las 7:00 AM, donde tus dedos se sienten rígidos como
              madera y tus rodillas crujen al primer paso, no es algo con lo que
              debas "aprender a vivir".
            </p>
            <p data-id="element-21">
              Has probado cremas que solo huelen fuerte, analgésicos que te
              irritan el estómago y suplementos costosos que prometen milagros
              pero no cambian nada.
            </p>
            <p className="font-bold text-brown-900" data-id="element-22">
              El problema es que esas soluciones solo tapan el síntoma, mientras
              la raíz del dolor sigue quemando tus tejidos por dentro.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-16 md:py-24 px-6" data-id="element-23">
        <div className="max-w-4xl mx-auto" data-id="element-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brown-900" data-id="element-25">
            ¿Por qué nada ha funcionado hasta ahora?
          </h2>
          <ul className="space-y-6 text-lg md:text-xl text-brown-800" data-id="element-26">
            {['Los analgésicos comunes apagan el dolor pero también detienen la reparación natural de tus cartílagos.', 'Las cremas tópicas no tienen la potencia necesaria para penetrar hasta la cápsula articular donde nace el problema.', 'Los suplementos de farmacia a menudo no se absorben porque les falta el "activador" correcto.', 'Muchos consejos "saludables" incluyen alimentos que, sin saberlo, están alimentando tu inflamación cada noche.'].map((item, i) => <li key={i} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm border border-cream-200" data-id="element-27">
                <X className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" aria-hidden="true" data-id="element-28" />
                <span data-id="element-29">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* SECTION 5 — THE UNIQUE MECHANISM */}
      <section className="py-16 md:py-24 px-6" data-id="element-30">
        <div className="max-w-4xl mx-auto bg-[#FDF5E6] border-2 border-amber-500/30 rounded-2xl p-8 md:p-12 shadow-lg" data-id="element-31">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-amber-600" data-id="element-32">
            El Protocolo S.O.S (Sinergia-Oxidación-Sedación)
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-brown-800" data-id="element-33">
            <p data-id="element-34">
              Este no es un libro de cocina común. Es un sistema basado en la
              activación química de tres ingredientes simples que ya tienes en
              tu despensa.
            </p>
            <p className="font-medium" data-id="element-35">
              Al combinarlos en la proporción exacta con el calor y la grasa
              adecuada, creas un "extintor celular" que viaja por tu sangre
              hasta tus articulaciones, disolviendo la rigidez mientras duermes.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6 — WHAT YOU GET */}
      <section className="py-16 md:py-24 px-6 bg-cream-200" data-id="element-36">
        <div className="max-w-4xl mx-auto" data-id="element-37">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brown-900" data-id="element-38">
            Todo lo que recibes hoy
          </h2>
          <ul className="space-y-6 text-lg md:text-xl text-brown-800" data-id="element-39">
            {[{
            title: 'La Guía Anti-Inflamación Dorada (PDF):',
            desc: 'El paso a paso detallado del protocolo.'
          }, {
            title: 'El Ritual del Amanecer:',
            desc: 'La receta exacta del tónico que "desbloquea" tus dedos en minutos.'
          }, {
            title: 'Calendario de Vitalidad de 7 Días:',
            desc: 'Qué comer desde el lunes para sentirte ligera el domingo.'
          }, {
            title: 'Lista de Compras "Cero Dolor":',
            desc: 'Para que sepas exactamente qué buscar en el supermercado.'
          }, {
            title: 'Micro-Ejercicios de Lubricación:',
            desc: 'Movimientos de 2 minutos que puedes hacer sentada.'
          }].map((item, i) => <li key={i} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm border border-amber-500/20" data-id="element-40">
                <Check className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" aria-hidden="true" data-id="element-41" />
                <div data-id="element-42">
                  <strong className="text-brown-900 block md:inline" data-id="element-43">
                    {item.title}
                  </strong>{' '}
                  <span data-id="element-44">{item.desc}</span>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* SECTION 7 — WHO THIS IS FOR / NOT FOR */}
      <section className="py-16 md:py-24 px-6" data-id="element-45">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-46">
          {/* For You */}
          <div className="bg-green-50/50 border-2 border-green-200 rounded-2xl p-8 md:p-10" data-id="element-47">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-green-800" data-id="element-48">
              Esto es para ti si...
            </h3>
            <ul className="space-y-6 text-lg text-brown-800" data-id="element-49">
              {['Mujeres de 65+ que quieren mantener su independencia física.', 'Abuelas que desean cargar a sus nietos sin soltar un quejido de dolor.', 'Personas que prefieren soluciones naturales sobre químicos agresivos.', 'Quienes sienten que la rigidez matutina les está robando la alegría de sus mañanas.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-50">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" aria-hidden="true" data-id="element-51" />
                  <span data-id="element-52">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Not For You */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-8 md:p-10" data-id="element-53">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-gray-700" data-id="element-54">
              Esto NO es para ti si...
            </h3>
            <ul className="space-y-6 text-lg text-gray-600" data-id="element-55">
              {['Quienes buscan una "pastilla mágica" sin cambiar ningún hábito alimenticio.', 'Personas que no están dispuestas a dedicar 5 minutos al día a su salud.', 'Quienes prefieren seguir dependiendo exclusivamente de fármacos de farmacia.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-56">
                  <X className="w-6 h-6 text-gray-400 flex-shrink-0 mt-1" aria-hidden="true" data-id="element-57" />
                  <span data-id="element-58">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 8 — HOW IT WORKS */}
      <section className="py-16 md:py-24 px-6 bg-cream-200" data-id="element-59">
        <div className="max-w-5xl mx-auto" data-id="element-60">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-brown-900" data-id="element-61">
            Así de simple funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-62">
            {[{
            step: '1',
            title: 'Descarga la Guía',
            desc: 'Recíbela al instante en tu correo o celular perfectamente legible.'
          }, {
            step: '2',
            title: 'Prepara tu Tónico',
            desc: 'Sigue las proporciones del Protocolo S.O.S con ingredientes de tu cocina.'
          }, {
            step: '3',
            title: 'Siente el Alivio',
            desc: 'Nota cómo el calor y la movilidad regresan a tus manos y rodillas cada mañana.'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center space-y-4" data-id="element-63">
                <div className="w-16 h-16 rounded-full bg-amber-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg" data-id="element-64">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold text-brown-900" data-id="element-65">
                  {item.title}
                </h3>
                <p className="text-lg text-brown-800" data-id="element-66">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 9 — PROOF/BELIEVABILITY */}
      <section className="py-16 md:py-24 px-6" data-id="element-67">
        <div className="max-w-4xl mx-auto" data-id="element-68">
          <div className="bg-amber-500/10 border-l-8 border-amber-500 p-8 md:p-12 rounded-r-2xl" data-id="element-69">
            <p className="text-xl md:text-2xl font-heading italic text-brown-900 leading-relaxed" data-id="element-70">
              "En los primeros 7 días, notarás que el 'tiempo de calentamiento'
              de tus articulaciones al despertar se reduce a la mitad. Lo que
              antes te tomaba una hora de dolor para empezar el día, ahora se
              resolverá en lo que tarda en colarse tu café."
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section className="py-16 md:py-24 px-6 bg-cream-200" data-id="element-71">
        <div className="max-w-3xl mx-auto" data-id="element-72">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-brown-900" data-id="element-73">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-74">
            {faqs.map((faq, i) => <div key={i} className="bg-white rounded-xl shadow-sm border border-cream-200 overflow-hidden" data-id="element-75">
                <button onClick={() => toggleFaq(i)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-expanded={openFaqIndex === i} data-id="element-76">
                  <span className="text-xl font-bold text-brown-900 pr-4" data-id="element-77">
                    {faq.q}
                  </span>
                  {openFaqIndex === i ? <ChevronUp className="w-6 h-6 text-amber-600 flex-shrink-0" data-id="element-78" /> : <ChevronDown className="w-6 h-6 text-amber-600 flex-shrink-0" data-id="element-79" />}
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === i ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-80">
                  <p className="text-lg text-brown-800" data-id="element-81">{faq.a}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 11 — OFFER + PRICE + CTA */}
      <section id="offer" className="py-16 md:py-24 px-6" data-id="element-82">
        <div className="max-w-4xl mx-auto text-center space-y-10" data-id="element-83">
          <img src={PRODUCT_IMAGE} alt="Guía Anti-Inflamación Dorada" className="w-full max-w-md mx-auto drop-shadow-2xl rounded-xl mb-8" data-id="element-84" />

          <p className="text-xl md:text-2xl text-brown-900 font-medium max-w-2xl mx-auto" data-id="element-85">
            Recupera tu libertad de movimiento hoy mismo por menos de lo que
            cuesta una cena fuera. No dejes que otro amanecer te atrape en la
            rigidez.
          </p>

          <div className="py-6" data-id="element-86">
            <p className="text-2xl text-brown-800 mb-2" data-id="element-87">
              Precio Especial de Lanzamiento:
            </p>
            <p className="text-2xl text-brown-800/60 line-through mb-1" data-id="element-88">
              $19.00 USD
            </p>
            <p className="text-5xl md:text-6xl font-bold text-amber-600 font-heading" data-id="element-89">
              $6.39 USD
            </p>
            <p className="mt-2 inline-block bg-amber-500/15 text-amber-700 font-bold text-lg px-4 py-1 rounded-full" data-id="element-90">
              ¡Ahorra más del 66%!
            </p>
          </div>

          <div className="max-w-2xl mx-auto space-y-4" data-id="element-91">
            <a href="https://vidalibresinlimites.com/cart/43760105914452:1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full px-6 py-5 md:py-6 bg-cta hover:bg-cta-hover text-white text-xl md:text-2xl font-bold rounded-xl shadow-xl transition-all duration-300 transform hover:scale-[1.02]" data-id="element-92">
              ¡SÍ! QUIERO MI GUÍA Y DESPERTAR SIN DOLOR
            </a>
            <div className="flex items-center justify-center gap-2 text-brown-800 text-lg" data-id="element-93">
              <Lock className="w-5 h-5 text-amber-600" data-id="element-94" />
              <span data-id="element-95">Pago seguro y descarga inmediata tras la compra.</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 12 — FOOTER */}
      <footer className="bg-brown-900 text-cream-100 py-12 px-6 text-center" data-id="element-96">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-97">
          <p className="text-lg opacity-90" data-id="element-98">
            © {new Date().getFullYear()} Guía Anti-Inflamación Dorada. Todos
            los derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm" data-id="element-99">
            <button onClick={() => setLegalModal('privacy')} className="underline underline-offset-2 opacity-80 hover:opacity-100 transition-opacity" data-id="element-100">
              Política de Privacidad
            </button>
            <span className="opacity-40" data-id="element-101">|</span>
            <button onClick={() => setLegalModal('terms')} className="underline underline-offset-2 opacity-80 hover:opacity-100 transition-opacity" data-id="element-102">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-sm opacity-70 max-w-3xl mx-auto leading-relaxed" data-id="element-103">
            Descargo de responsabilidad: La información proporcionada en este
            sitio web y en la guía es solo para fines educativos e informativos.
            No pretende sustituir el consejo, diagnóstico o tratamiento médico
            profesional. Siempre busque el consejo de su médico u otro proveedor
            de salud calificado con cualquier pregunta que pueda tener sobre una
            condición médica.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      {legalModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setLegalModal(null)} data-id="element-104">
          <div className="bg-cream-100 text-brown-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-105">
            <div className="sticky top-0 bg-cream-100 border-b border-amber-500/20 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-106">
              <h2 className="text-2xl font-bold font-heading" data-id="element-107">
                {legalModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h2>
              <button onClick={() => setLegalModal(null)} className="p-2 rounded-full hover:bg-brown-900/10 transition-colors" aria-label="Cerrar" data-id="element-108">
                <X className="w-6 h-6" data-id="element-109" />
              </button>
            </div>

            <div className="px-6 py-8 space-y-6 text-lg leading-relaxed" data-id="element-110">
              {legalModal === 'privacy' ? <>
                  <p className="text-sm text-brown-800/60" data-id="element-111">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-112">
                    1. Información que Recopilamos
                  </h3>
                  <p data-id="element-113">
                    Recopilamos únicamente la información necesaria para
                    procesar su compra y entregar el producto digital,
                    incluyendo: nombre, dirección de correo electrónico e
                    información de pago.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-114">
                    2. Uso de la Información
                  </h3>
                  <p data-id="element-115">
                    Su información personal se utiliza exclusivamente para:
                    procesar su pedido, enviar el producto digital adquirido y
                    comunicarnos con usted sobre su compra si es necesario.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-116">
                    3. Protección de Datos
                  </h3>
                  <p data-id="element-117">
                    Implementamos medidas de seguridad para proteger su
                    información personal. Los pagos se procesan a través de
                    plataformas seguras con encriptación SSL.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-118">
                    4. No Compartimos su Información
                  </h3>
                  <p data-id="element-119">
                    No vendemos, intercambiamos ni transferimos su información
                    personal a terceros. Esto no incluye terceros de confianza
                    que nos ayudan a operar nuestro sitio web, siempre que
                    acuerden mantener esta información confidencial.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-120">5. Cookies</h3>
                  <p data-id="element-121">
                    Nuestro sitio puede utilizar cookies para mejorar su
                    experiencia de navegación. Puede optar por desactivar las
                    cookies en la configuración de su navegador.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-122">
                    6. Sus Derechos
                  </h3>
                  <p data-id="element-123">
                    Usted tiene derecho a solicitar acceso, corrección o
                    eliminación de sus datos personales en cualquier momento.
                  </p>
                </> : <>
                  <p className="text-sm text-brown-800/60" data-id="element-124">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-125">
                    1. Aceptación de los Términos
                  </h3>
                  <p data-id="element-126">
                    Al acceder a este sitio web y realizar una compra, usted
                    acepta cumplir con estos términos y condiciones de uso.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-127">
                    2. Producto Digital
                  </h3>
                  <p data-id="element-128">
                    La "Guía Anti-Inflamación Dorada" es un producto digital en
                    formato PDF. Una vez completada la compra, recibirá acceso
                    inmediato para descargar el material.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-129">
                    3. Uso del Producto
                  </h3>
                  <p data-id="element-130">
                    El contenido adquirido es para uso personal únicamente.
                    Queda prohibida la reproducción, distribución o reventa del
                    material sin autorización expresa.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-131">
                    4. Descargo de Responsabilidad Médica
                  </h3>
                  <p data-id="element-132">
                    La información contenida en esta guía es solo para fines
                    educativos e informativos. No sustituye el consejo médico
                    profesional. Siempre consulte con su médico antes de iniciar
                    cualquier protocolo de salud, especialmente si toma
                    medicamentos o tiene condiciones médicas preexistentes.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-133">
                    5. Política de Reembolso
                  </h3>
                  <p data-id="element-134">
                    Debido a la naturaleza digital del producto, todas las
                    ventas son finales. Sin embargo, si experimenta problemas
                    técnicos para acceder al material, nos comprometemos a
                    resolverlos.
                  </p>
                  <h3 className="text-xl font-bold font-heading" data-id="element-135">
                    6. Limitación de Responsabilidad
                  </h3>
                  <p data-id="element-136">
                    No nos hacemos responsables de los resultados individuales
                    obtenidos al seguir las recomendaciones de la guía. Los
                    resultados pueden variar de persona a persona.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </main>;
}